import { assert, error, log } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { appSync } from '../app/app_sync.js';
import { friends } from '../data/friends.js';
import { PanelBase } from '../ui/panel_base.js';
import { raceWithEvents } from '../util/event_racer.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { storage } from '../app/storage.js';

import '../components/trip_options.js';
import '../controls/editable_text.js';
import '../controls/weather_control.js';
import './checklist_view_edit.js';
import './checklist_view_pack.js';
import './food_menu_view.js';

/**
 * The trip panel. Used to manage trip title, options, weather and a checklist.
 */
class TripPanel extends PanelBase {
  static id = navigation.kTripPanelId;

  static get selectors() {
    return Object.assign({}, super.selectors, {
      BackButton: '.back-to-trips-button',
      ChecklistTab: '#trip-checklist-tab',
      ChecklistTabLabel: '#trip-checklist-tab + label',
      ChecklistViewEdit: 'checklist-view-edit',
      ChecklistViewPack: 'checklist-view-pack',
      FoodMenuTab: '#trip-food-menu-tab',
      FoodMenuTabLabel: '#trip-food-menu-tab + label',
      FoodMenuView: '#food-menu-view',
      Options: '#trip-options',
      PackTab: '#trip-pack-tab',
      PackTabLabel: '#trip-pack-tab + label',
      Title: '#trip-title',
      Weather: '#weather',
    });
  }

  static get elements() {
    return {
      backButton: this.selectors.BackButton,
      checklistTab: this.selectors.ChecklistTab,
      checklistViewEdit: this.selectors.ChecklistViewEdit,
      checklistViewPack: this.selectors.ChecklistViewPack,
      foodMenuTab: this.selectors.FoodMenuTab,
      foodMenuView: this.selectors.FoodMenuView,
      optionsElement: this.selectors.Options,
      packTab: this.selectors.PackTab,
      titleElement: this.selectors.Title,
      weatherElement: this.selectors.Weather,
    };
  }

  prepare() {
    listenToEvent(this.titleElement, 'valuechange', ev =>
      this.#trip.setTitle(ev.detail)
    );

    listenToEvent(this.ownerDocument, 'checklist-change', () =>
      appSync.syncTrip.notifyChecklistChanged()
    );

    listenToEvent(this.ownerDocument, 'food-menu-change', () =>
      appSync.syncTrip.notifyFoodMenuChanged()
    );

    this.optionsElement.friends = friends;
    this.optionsElement.plainStyle = true;
    listenToEvent(this.optionsElement, 'optionchange', ({ detail }) =>
      this.#trip.setOption(detail.name, detail.value)
    );
    this.optionsElement.invitesUpdated = async ({
      invitees,
      revokedInviteHashes,
    }) => {
      await this.#trip.shareOrRevoke({ invitees, revokedInviteHashes });
      // TODO: <trip-options/> should be piped to a trip.
      this.optionsElement.cocampers = this.#trip.cocampers;
    };

    const locControlConsumerId = this.optionsElement.consumerId;
    navigation.map.setConsumer(locControlConsumerId, loc => {
      this.optionsElement.setLocationTo(loc);
      navigation.closeMap();
    });

    this.weatherElement.loadingTimeout = PanelBase.NoLoadingTimeout;
    this.checklistViewEdit.loadingTimeout = PanelBase.NoLoadingTimeout;
    this.checklistViewPack.loadingTimeout = PanelBase.NoLoadingTimeout;

    // Trun the loading mode on. When the data will be set, the mode will be
    // turned off.
    this.loadingOn();
  }

  async set(trip) {
    assert(trip, `[trip] no trip to set`);
    log(`[trip] set to ${trip.id}`);

    this.loadingOn();

    if (this.#trip) {
      this.#trip.unpipe(this.#dataMap);
    }
    this.#trip = trip;
    this.#trip.pipe(this.#dataMap);

    this.setOptions(trip);
    this.loadingOff();

    let status = this.#getTripStatus(this.#trip) || 'checklist';
    this.setStatus(status, 'doNotStore');
    switch (status) {
      case 'menu':
        this.foodMenuTab.checked = true;
        break;
      case 'packgear':
        this.packTab.checked = true;
        break;
      default:
        // checklist
        this.checklistTab.checked = true;
        break;
    }
  }

  setStatus(status, doNotStore) {
    log(`[trip] '${status}' status is on`);
    switch (status) {
      case 'menu':
        this.setAttribute('view', 'food-menu');
        this.foodMenuView.set(this.#trip.entities.menu);
        break;

      case 'packgear':
        this.setAttribute('view', 'checklist-pack');
        this.checklistViewPack.set(this.#trip.entities.checklist);
        break;

      case 'checklist':
        this.setAttribute('view', 'checklist-edit');
        this.checklistViewEdit.set(this.#trip.entities.checklist);
        break;

      default:
        error(`[trip] unrecognized '${status}' status`);
    }
    if (!doNotStore) {
      this.#setTripStatus(this.#trip, status);
    }
  }

  initialize() {
    super.initialize();

    listenToEvent(
      this.backButton,
      'click',
      () =>
        !this.backButton.hasAttribute('disabled') && navigation.toTripPlanning()
    );
    listenToEvent(this.checklistTab, 'input', () => {
      this.setStatus('checklist');
      return this.#trip.entities.checklist.learn();
    });
    listenToEvent(this.foodMenuTab, 'input', () => this.setStatus('menu'));
    listenToEvent(this.packTab, 'input', () => this.setStatus('packgear'));
  }

  static markup = `
    <!-- Header -->
    <div id='trip-header'>
      <!-- Back button -->
      <img class='button back-to-trips-button'
           src='/images/back.png'
           title='Back to trip planning'>

      <!-- Title and options -->
      <editable-text id='trip-title'></editable-text>
      <div class='loading-box trip-title-loading-box'>
        <div class='loading-bar'></div>
      </div>
      <trip-options id='trip-options'
                    going-with-type='link'></trip-options>
      <div class='loading-box trip-options-loading-box'>
        <div class='loading-bar'></div>
        <div class='loading-bar'></div>
      </div>

      <!-- Weather widget -->
      <div class='embrace weather-container'>
        <weather-control id='weather'></weather-control>
      </div>

      <!-- Tabs -->
      <div class='tabs'>
        <input type='radio'
               name='trip-tab'
               id='trip-checklist-tab'>
        <label for='trip-checklist-tab'
               class='tab'
               title='Edit the checklist'>
          <img role='none'>
          <span class='tab-label'>Checklist</span>
        </label>
        <input type='radio'
               name='trip-tab'
               id='trip-food-menu-tab'>
        <label for='trip-food-menu-tab'
               class='tab'
               title='Make a menu'>
          <img role='none'>
          <span class='tab-label'>Menu</span>
        </label>
        <input type='radio'
               name='trip-tab'
               id='trip-pack-tab'>
        <label for='trip-pack-tab'
               class='tab'
               title='Pack gear'>
          <img role='none'>
          <span class='tab-label'>Pack Gear</span>
        </label>
      </div>
    </div>

    <!-- Checklist editor -->
    <checklist-view-edit id='editor'
                         class='checklist-view grouping'>
    </checklist-view-edit>

    <!-- Checklist editor loading block -->
    <div class='loading-container checklist-view grouping'>
      <div class='editor-loading-box loading-box'>
        <div class='loading-bar w80'></div>
        <div class='loading-bar w60 indent'></div>
        <div class='loading-bar w40 indent'></div>
        <div class='loading-bar w50 indent'></div>
        <div class='loading-bar w20 indent'></div>
        <div class='loading-bar w50 indent'></div>
        <div class='loading-bar w40 indent'></div>
      </div>
      <div class='editor-loading-box loading-box'>
        <div class='loading-bar w80'></div>
        <div class='loading-bar w60 indent'></div>
        <div class='loading-bar w40 indent'></div>
        <div class='loading-bar w50 indent'></div>
        <div class='loading-bar w20 indent'></div>
        <div class='loading-bar w50 indent'></div>
      </div>
    </div>

    <!-- Food menu -->
    <food-menu-view id='food-menu-view'></food-menu-view>

    <!-- Checklist pack -->
    <checklist-view-pack id='checklist-pack'
                         class='checklist-view'>
    </checklist-view-pack>
  `;

  get historyState() {
    return [{ page: this.page }, '', ''];
  }

  switchOn(options) {
    if (options && options.loading) {
      this.loadingOn();
    }
    // Focus the checklist editor after the trip is loaded (if applicable).
    super.switchOn();
    this.focusChecklistEditor();
  }

  isChecklistOn() {
    return this.getAttribute('view') == 'checklist-edit';
  }

  isPackGearOn() {
    return this.getAttribute('view') == 'checklist-pack';
  }

  isFoodMenuOn() {
    return this.getAttribute('view') == 'food-menu';
  }

  isOpenFor(tripIdentifier) {
    return this.#trip && tripIdentifier.id == this.#trip.id;
  }

  syncChecklist(tripIdentifier) {
    // Sync checklist if it's active.
    if (this.isOpenFor(tripIdentifier)) {
      if (this.isChecklistOn()) {
        this.checklistViewEdit.sync();
      } else if (this.isPackGearOn()) {
        this.checklistViewPack.sync();
      }
    }
  }

  syncFoodMenu(tripIdentifier) {
    // Sync food menu if it's active.
    if (this.isOpenFor(tripIdentifier) && this.isFoodMenuOn()) {
      this.foodMenuView.sync();
    }
  }

  setOptions(options) {
    this.optionsElement.set(options);
    this.optionsElement.setAttribute('hasvalue', 'hasvalue');
  }

  setWeather({ loc, startDate, duration }) {
    this.weatherElement.set({ loc, startDate, duration });
  }

  selectTitle() {
    this.titleElement.select();
  }
  showMap() {
    this.optionsElement.showMap();
  }

  clear() {
    this.titleElement.value = '';
    this.titleElement.readonly = true;
    this.checklistViewEdit.clear();
    this.checklistViewPack.clear();
  }

  get headerReadonly() {
    return this.titleElement.readonly;
  }

  set headerReadonly(v) {
    this.titleElement.readonly = v;
    this.optionsElement.readonly = v;
  }

  get editorReadonly() {
    return this.checklistViewEdit.contentEditable;
  }

  set editorReadonly(v) {
    this.checklistViewEdit.contentEditable = !v;
  }

  /**
   * Focus the editor after data load (if data is loading and if the focus wasn't
   * moved away while waiting).
   */
  focusChecklistEditor() {
    log(`[trip] schedule focus checklist editor`);
    raceWithEvents({
      action: () => {
        log(`[trip] focus checklist editor`);
        this.checklistViewEdit.focus();
      },
      actionPromise: this.loadingPromise.then(
        () => this.checklistViewEdit.loadingPromise
      ),
      element: document,
      events: ['focus'],
    });
  }

  // UI loading
  loadingOn() {
    super.loadingOn();

    let view = this.getAttribute('view');
    switch (view) {
      case 'food-menu':
        this.foodMenuView.loadingOn();
        break;
      case 'checklist-pack':
        this.checklistViewPack.loadingOn();
        break;
      default:
        // 'checklist-edit'
        this.checklistViewEdit.loadingOn();
    }
  }

  loadingTimeout = TripPanel.NoLoadingTimeout;

  static uiname = 'TripPanel';

  #getTripStatus(trip) {
    return storage.get(`trip-status:${trip.stringIdentifier}`);
  }

  #setTripStatus(trip, status) {
    storage.set(`trip-status:${trip.stringIdentifier}`, status);
  }

  #trip = null;

  #dataMap = {
    title: v => (this.titleElement.value = v),
    rights: v =>
      (this.headerReadonly = this.editorReadonly = !v || !v.includes('write')),
    locNDates: v => this.setWeather(v),
  };

  #loadingPromise;
  #onTripOptionChanged;
}

navigation.registerPanel(TripPanel);

export { TripPanel };
